import ClickOutside from "vue-click-outside";
import { ADVANCED_FIELD_TYPES, PAGES } from "@consts";

export default {
	name: "AdvancedSearchControl",
	directives: {
		ClickOutside,
	},
	props: {
		fieldType: {
			type: String,
			required: true,
		},
		value: {
			type: String,
			required: false,
			default: "",
		},
	},
	data() {
		return {
			showHelpWindow: false,
			fieldHasFocus: false,
		};
	},
	watch: {
		value(newVal) {
			// When the parent sanitizes invalid input (e.g. strips a digit from a
			// letters-only field), the sanitized value can equal the previous
			// value, so Vue sees no reactive change and skips the DOM update —
			// leaving the invalid character visible. We fix it here by syncing
			// the input's DOM value manually whenever it drifts from the prop,
			// while preserving the cursor position.
			const input = this.$el.querySelector("input");
			if (!input || input.type === "number" || input.value === newVal) {
				return;
			}
			const start = input.selectionStart;
			const diff = input.value.length - newVal.length;
			input.value = newVal;
			const newPos = Math.max(0, start - diff);
			input.setSelectionRange(newPos, newPos);
		},
	},
	computed: {
		isWordLengthField() {
			return this.fieldType === ADVANCED_FIELD_TYPES.LENGTH;
		},
		isContainsField() {
			return this.fieldType === ADVANCED_FIELD_TYPES.WITH;
		},
		fieldId() {
			return `asc_input_${this.fieldType}`;
		},
		inputType() {
			return this.isWordLengthField ? "number" : "search";
		},
		fieldTitle() {
			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
					return this.$t("AdvancedSearchStartsWithAriaLabel");
				case ADVANCED_FIELD_TYPES.ENDS:
					return this.$t("AdvancedSearchEndsWithAriaLabel");
				case ADVANCED_FIELD_TYPES.WITH:
					return this.$t("AdvancedSearchContainsAriaLabel");
				case ADVANCED_FIELD_TYPES.LENGTH:
					return this.$t("AdvancedSearchLengthTitleAttribute");
				default:
					return this.fieldType;
			}
		},
		fieldLabel() {
			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
					if (
						this.$route?.name ===
						PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS
					) {
						return "Enter your starting letters";
					}
					return this.$t("AdvancedSearchStartsTextB");
				case ADVANCED_FIELD_TYPES.ENDS:
					if (
						this.$route?.name ===
						PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS
					) {
						return "Enter your ending letters";
					}
					return this.$t("AdvancedSearchEndsTextB");
				case ADVANCED_FIELD_TYPES.WITH:
					if (
						this.$route?.name ===
						PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING
					) {
						return "Enter your letters";
					}
					return this.$t("AdvancedSearchContainsText");
				case ADVANCED_FIELD_TYPES.LENGTH:
					return this.$t("AdvancedSearchLengthText");
				default:
					return this.fieldType;
			}
		},
		fieldAriaLabel() {
			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
					return this.$t("AdvancedSearchStartsWithAriaLabel");
				case ADVANCED_FIELD_TYPES.ENDS:
					return this.$t("AdvancedSearchEndsWithAriaLabel");
				case ADVANCED_FIELD_TYPES.WITH:
					return this.$t("AdvancedSearchContainsAriaLabel");
				case ADVANCED_FIELD_TYPES.LENGTH:
					return this.$t("AdvancedSearchLengthTooltipAriaLabel");
				default:
					return null;
			}
		},
		placeholder() {
			if (this.isWordsByLengthLandingPage) {
				return null;
			}

			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
					return "A";
				case ADVANCED_FIELD_TYPES.ENDS:
					return "B";
				case ADVANCED_FIELD_TYPES.WITH:
					return "A_B";
				default:
					return null;
			}
		},
		fieldMinValue() {
			return this.isWordLengthField ? 2 : null;
		},
		fieldMaxValue() {
			return this.isWordLengthField ? 20 : null;
		},
		fieldMaxLength() {
			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
				case ADVANCED_FIELD_TYPES.ENDS:
				case ADVANCED_FIELD_TYPES.WITH:
					return 20;
				case ADVANCED_FIELD_TYPES.LENGTH:
					return 2;
				default:
					return 0;
			}
		},
		fieldHasInput() {
			return this.value?.length > 0;
		},
		helpContent() {
			switch (this.fieldType) {
				case ADVANCED_FIELD_TYPES.STARTS:
					return (
						"<div class='relative'>" +
						"<span class='explanation'>See words that start with these letters in <strong>this order</strong></span>" +
						"<span class='example'> <strong>AS</strong>" +
						"<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>\n" +
						"<path d='M12.95 12.1685L8 7.2185L9.2185 6L15.387 12.1685L9.2185 18.337L8 17.1185L12.95 12.1685Z' fill='black'/>\n" +
						"</svg>" +
						"<strong>As</strong>sert</span>" +
						"</div>"
					);
				case ADVANCED_FIELD_TYPES.ENDS:
					return (
						"<div class='relative'>" +
						"<span class='explanation'>See words that <strong> end </strong> with these letters </span>" +
						"<span class='example'> <strong>AS</strong> " +
						"<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>\n" +
						"<path d='M12.95 12.1685L8 7.2185L9.2185 6L15.387 12.1685L9.2185 18.337L8 17.1185L12.95 12.1685Z' fill='black'/>\n" +
						"</svg>" +
						" Are<strong>as</strong></span>" +
						"</div>"
					);
				case ADVANCED_FIELD_TYPES.WITH:
					return (
						"<div class='relative'>" +
						"<span class='explanation'> See words that contain these letters in <strong>this order</strong> </span>" +
						"<span class='example'> <strong>AS</strong>" +
						"<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>\n" +
						"<path d='M12.95 12.1685L8 7.2185L9.2185 6L15.387 12.1685L9.2185 18.337L8 17.1185L12.95 12.1685Z' fill='black'/>\n" +
						"</svg>" +
						" <strong>As</strong>tray</span>" +
						"<span class='explanation'> Or in certain positions </span>" +
						"<span class='example'> <strong>A_S</strong>" +
						"<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>\n" +
						"<path d='M12.95 12.1685L8 7.2185L9.2185 6L15.387 12.1685L9.2185 18.337L8 17.1185L12.95 12.1685Z' fill='black'/>\n" +
						"</svg>" +
						" Ye<strong>a</strong>r<strong>s</strong></span>" +
						"</div>"
					);
				case ADVANCED_FIELD_TYPES.LENGTH:
					return (
						"<div class='relative'>" +
						"<span class='explanation'>See words that have a <strong>specific word length</strong></span>" +
						"</div>"
					);
			}

			return "";
		},
		isResultsPage() {
			return this.$store.getters["navigation/isResultsPage"];
		},
		isWordsByLengthLandingPage() {
			return this.$store.getters["navigation/isWordsByLengthLandingPage"];
		},
		isSingleFieldWordLengthLandingPage() {
			return [
				PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS,
				PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS,
				PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING,
			].includes(this.$route.name);
		},
		showField() {
			const isWordsByLengthLandingPage =
				this.$store.getters["navigation/isWordsByLengthLandingPage"];

			if (!isWordsByLengthLandingPage) {
				return true;
			}

			if (
				this.$route.name === PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS &&
				this.fieldType !== ADVANCED_FIELD_TYPES.STARTS
			) {
				return false;
			}

			if (
				this.$route.name === PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS &&
				this.fieldType !== ADVANCED_FIELD_TYPES.ENDS
			) {
				return false;
			}

			if (
				this.$route.name === PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING &&
				this.fieldType !== ADVANCED_FIELD_TYPES.WITH
			) {
				return false;
			}

			if (
				isWordsByLengthLandingPage &&
				this.fieldType === ADVANCED_FIELD_TYPES.LENGTH
			) {
				return false;
			}

			return true;
		},
	},
	methods: {
		onFieldInput(event) {
			const input = event.target;
			const cursorPos = input.selectionStart;
			const typedLength = input.value.length;
			this.$emit("input", input.value);

			// After the parent processes the emit, check whether the DOM still
			// matches the accepted value. There are two distinct cases:
			//
			// 1. Valid character typed: the parent updates `this.value` to the
			//    new string, the value watcher below handles cursor preservation,
			//    and nothing extra is needed here.
			//
			// 2. Invalid character typed (e.g. a digit in a letters-only field):
			//    the parent sanitizes back to the *same* value it already held,
			//    so no reactive change occurs and Vue never re-renders — the
			//    invalid character stays in the DOM. The nextTick callback below
			//    catches this case by comparing the DOM value to `this.value`
			//    after Vue has had a chance to run.
			this.$nextTick(() => {
				if (input.value !== this.value) {
					const diff = typedLength - this.value.length;
					input.value = this.value;
					const newPos = Math.max(0, cursorPos - diff);
					input.setSelectionRange(newPos, newPos);
				}
			});
		},
		onFieldChange(event) {
			const fieldLabel = this.fieldLabel.replace(":", "").toLowerCase();
			const eventID = {
				[ADVANCED_FIELD_TYPES.WITH]: 54,
				[ADVANCED_FIELD_TYPES.ENDS]: 55,
				[ADVANCED_FIELD_TYPES.LENGTH]: 56,
				[ADVANCED_FIELD_TYPES.STARTS]: 57,
			};

			this.$store.dispatch("tracking/trackEvent", {
				eventCategory: "Advanced Search",
				eventAction: `Change ${fieldLabel} value`,
				eventLabel: event.target.value,
				eventID: eventID[this.fieldType],
			});
		},
		onFieldFocus() {
			this.fieldHasFocus = true;
			if (this.isWordLengthField) {
				this.$store.dispatch("tracking/trackEvent", {
					eventCategory: "Advanced Search",
					eventAction: "Click menu",
					eventLabel: this.$t("AdvancedSearchLengthText"),
					eventID: 74,
				});
			}
		},
		onFieldBlur() {
			this.fieldHasFocus = false;
		},
		onClearButtonClick() {
			const fieldLabel = this.fieldLabel.replace(":", "").toLowerCase();
			const eventID = {
				[ADVANCED_FIELD_TYPES.WITH]: 59,
				[ADVANCED_FIELD_TYPES.ENDS]: 62,
				[ADVANCED_FIELD_TYPES.LENGTH]: 72,
				[ADVANCED_FIELD_TYPES.STARTS]: 83,
			};
			this.$store.dispatch("tracking/trackEvent", {
				eventCategory: "Advanced Search",
				eventAction: `Click ${fieldLabel} clear button`,
				eventID: eventID[this.fieldType],
			});
			this.$emit("input", "");
		},
		onHelpButtonClick() {
			const fieldLabel = this.fieldLabel.replace(":", "").toLowerCase();
			const eventID = {
				[ADVANCED_FIELD_TYPES.WITH]: 60,
				[ADVANCED_FIELD_TYPES.ENDS]: 63,
				[ADVANCED_FIELD_TYPES.LENGTH]: 73,
				[ADVANCED_FIELD_TYPES.STARTS]: 84,
			};
			this.$store.dispatch("tracking/trackEvent", {
				eventCategory: "Advanced Search",
				eventAction: `Click ${fieldLabel} help button`,
				eventID: eventID[this.fieldType],
			});
			this.showHelpWindow = !this.showHelpWindow;
		},
		onHelpWindowClickOutside(e) {
			const isButtonClick =
				e.target === this.$refs.helpButton ||
				e.composedPath().includes(this.$refs.helpButton);
			if (!isButtonClick) {
				this.showHelpWindow = false;
			}
		},
	},
};
